import { useState, useEffect, useCallback } from 'react';
import { Dialog, Button, Input, HStack, VStack, Portal, CloseButton } from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import TextEditor from '@/components/ui/text-editor';
import ContentSelector from './elements/content-selector';
import useContentTypesStore from '@/utils/use-content-type';
import api from '@/utils/api';
import { Event, Content } from '@/lib/types';

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  event_date: z.string().min(1, 'Date is required'),
  event_time: z.string().min(1, 'Time is required'),
  description: z.string().optional(),
  contents: z.array(z.string()).optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId?: number | null;
  selectedDate?: string | null;
  onSuccess: () => void;
}

export default function EventModal({
  isOpen,
  onClose,
  eventId,
  selectedDate,
  onSuccess,
}: EventModalProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);
  const [contents, setContents] = useState<Content[]>([]);
  const { contentTypes } = useContentTypesStore();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    control,
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: '',
      event_date: selectedDate || format(new Date(), 'yyyy-MM-dd'),
      event_time: '',
      description: '',
    },
  });

  const description = useController({
    control,
    name: 'description',
    defaultValue: '',
  });

  const content = useController({
    control,
    name: 'contents',
    defaultValue: [],
  });

  const loadEventData = useCallback(async () => {
    if (!eventId) return;

    setIsLoading(true);
    try {
      const response = await api.get(`/calendars/${eventId}`);
      const event: Event = response.data;

      if (!event) return;

      const eventDate = format(new Date(), 'yyyy-MM-dd');
      const formattedDate = event.event_date
        ? new Date(event.event_date).toISOString().split('T')[0]
        : eventDate;
      const contents = event.contents || [];

      setValue('title', event.title || '');
      setValue('event_date', formattedDate);
      setValue('event_time', event.event_time || '');
      setValue('description', event.description || '');
      setValue('contents', contents.map((content: any) => content.reference_id) || []);
      setContents(contents);
      setEvent(event);
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to load event',
        description: error.response?.data?.message || 'Please try again',
      });
    } finally {
      setIsLoading(false);
    }
  }, [eventId, setValue]);

  useEffect(() => {
    if (isOpen && eventId) {
      loadEventData();
    }
    if (isOpen && !eventId && selectedDate) {
      setValue('event_date', selectedDate);
    }
  }, [isOpen, selectedDate, setValue, eventId, loadEventData]);

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => reset(), 300);
    }
  }, [isOpen, reset]);

  const onSubmit = async (data: EventFormData) => {
    if (!workspaceId) return;

    setIsSubmitting(true);

    try {
      const [year, month, day] = data.event_date.split('-').map((part) => parseInt(part, 10));
      const eventDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));

      const payload = {
        workspace_id: parseInt(workspaceId, 10),
        title: data.title,
        description: data.description,
        event_date: eventDate.toISOString().split('T')[0],
        event_time: data.event_time,
        status: event?.status || 'draft',
        contents: {
          ref_ids: data.contents || [],
        },
      };

      if (eventId) {
        await api.put(`calendars/${eventId}`, payload);
        toaster.create({
          type: 'success',
          title: 'Event updated successfully',
        });
      } else {
        await api.post('calendars', payload);
        toaster.create({
          type: 'success',
          title: 'Event created successfully',
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      toaster.create({
        type: 'error',
        title: 'Failed to save event',
        description: error.response?.data?.message || 'Please check your information and try again',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorChange = (value: string) => {
    setValue('description', value, { shouldValidate: true });
  };

  const applyChanges = (contentRefs: string[], item?: Content) => {
    content.field.onChange(contentRefs);

    if (item) {
      setContents((prev) => [...prev, item]);
    } else {
      setContents((prev) => prev.filter((content) => contentRefs.includes(content.reference_id)));
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={onClose}
      size='xl'
      placement='center'
      motionPreset='slide-in-bottom'
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>{eventId ? 'Edit Event' : 'Create New Event'}</Dialog.Title>
            </Dialog.Header>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Dialog.Body>
                <VStack gap={4} align='stretch'>
                  <Field label='Title' invalid={!!errors.title} errorText={errors.title?.message}>
                    <Input
                      {...register('title')}
                      placeholder='Event title'
                      disabled={isLoading || isSubmitting}
                    />
                  </Field>

                  <HStack gap={4} align='start'>
                    <Field
                      label='Date'
                      invalid={!!errors.event_date}
                      errorText={errors.event_date?.message}
                    >
                      <Input
                        {...register('event_date')}
                        type='date'
                        disabled={isLoading || isSubmitting}
                      />
                    </Field>
                    <Field
                      label='Time'
                      invalid={!!errors.event_time}
                      errorText={errors.event_time?.message}
                    >
                      <Input
                        {...register('event_time')}
                        type='time'
                        disabled={isLoading || isSubmitting}
                      />
                    </Field>
                  </HStack>

                  {workspaceId && (
                    <ContentSelector
                      contentRefs={content.field.value || []}
                      contentTypes={contentTypes}
                      workspaceId={parseInt(workspaceId, 10)}
                      contents={contents || []}
                      onChange={applyChanges}
                    />
                  )}

                  <Field label='Description'>
                    <TextEditor
                      initialValue={description.field.value}
                      onChange={handleEditorChange}
                    />
                  </Field>
                </VStack>
              </Dialog.Body>

              <Dialog.Footer>
                <HStack gap={3}>
                  <Button onClick={onClose} variant='ghost' disabled={isSubmitting}>
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    colorPalette='brand'
                    loading={isSubmitting}
                    loadingText={eventId ? 'Saving...' : 'Creating...'}
                  >
                    {eventId ? 'Save Changes' : 'Create Event'}
                  </Button>
                </HStack>
              </Dialog.Footer>

              <Dialog.CloseTrigger asChild>
                <CloseButton size='sm' />
              </Dialog.CloseTrigger>
            </form>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
